/** Paleta contrastada para distinguir tenants (usuarios multi-empresa). */
export const TENANT_ACCENT_PALETTE = [
  '#0d9488',
  '#c2410c',
  '#7c3aed',
  '#0369a1',
  '#b45309',
  '#be185d',
  '#15803d',
  '#4338ca',
  '#b91c1c',
  '#0f766e',
  '#a16207',
  '#6d28d9',
] as const;

function hashSeed(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

/**
 * Color de acento estable por tenant (derivado de id).
 * Sin persistencia ni config en API.
 */
export function tenantAccentColor(seed: string | number | null | undefined): string {
  const key = seed == null || seed === '' ? '0' : String(seed);
  return TENANT_ACCENT_PALETTE[hashSeed(key) % TENANT_ACCENT_PALETTE.length];
}

/**
 * Asigna colores únicos dentro del set de tenants del usuario.
 * Seed anclado a tenantId; si hay colisión, desplaza al siguiente de la paleta.
 */
export function tenantAccentMap(
  tenants: Array<{ id: number }>
): Record<number, string> {
  const used = new Set<number>();
  const map: Record<number, string> = {};
  const sorted = [...tenants].sort((a, b) => a.id - b.id);

  for (const t of sorted) {
    let idx = hashSeed(String(t.id)) % TENANT_ACCENT_PALETTE.length;
    let attempts = 0;
    while (used.has(idx) && attempts < TENANT_ACCENT_PALETTE.length) {
      idx = (idx + 1) % TENANT_ACCENT_PALETTE.length;
      attempts += 1;
    }
    used.add(idx);
    map[t.id] = TENANT_ACCENT_PALETTE[idx];
  }

  return map;
}

/**
 * Color configurado por la empresa (Administración → Apariencia) > color derivado del id.
 * El derivado solo aplica a usuarios multi-empresa; el configurado aplica siempre.
 */
export function resolveTenantAccent(
  tenantId: number | null | undefined,
  tenants: Array<{ id: number }>,
  configured: Record<number, string> = {}
): string | null {
  if (tenantId == null) return null;
  if (configured[tenantId]) return configured[tenantId];
  if (tenants.length <= 1) return null;
  const map = tenantAccentMap(tenants);
  return map[tenantId] ?? tenantAccentColor(tenantId);
}
